/**
 * AccessDeniedPage Component
 *
 * Shown when a user tries to access a route they don't have permission for.
 * - "Volver" button always visible
 * - "Ir al inicio" button only visible if user has at least one accessible page
 * - Centered on full screen
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldExclamationIcon, ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import { MODULES_CONFIG } from '@/config/modules-config'

interface AccessDeniedPageProps {
  module?: string
  action?: string
  /** Custom message to show instead of the default */
  message?: string
}

type BackendPermissions = Record<string, Record<string, boolean>>

/** Find first accessible href by scanning MODULES_CONFIG (same logic as SmartRedirect) */
function findFirstAccessibleHref(permissions: BackendPermissions | undefined): string | null {
  if (!permissions) return null

  for (const module of MODULES_CONFIG) {
    if (module.requiredPermission) {
      const [mod, action] = module.requiredPermission.split('.')
      if (permissions[mod]?.[action] !== true) continue
    }

    for (const item of module.items) {
      if (item.requiredPermission) {
        const [mod, action] = item.requiredPermission.split('.')
        if (permissions[mod]?.[action] !== true) continue
      }

      if (permissions[module.id]?.[item.id] === true) {
        return item.href
      }
    }
  }

  return null
}

export function AccessDeniedPage({ module, action, message }: AccessDeniedPageProps) {
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: permissions } = useQuery<BackendPermissions>({
    queryKey: ['permissions', 'me', 'access-denied'],
    queryFn: async () => {
      const data = await apiClient.get<BackendPermissions>('/auth/permissions/me/')
      return data || {}
    },
    staleTime: 60 * 1000,
  })

  const firstAccessibleHref = useMemo(() => {
    return findFirstAccessibleHref(permissions)
  }, [permissions])

  return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)] px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
          <ShieldExclamationIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Acceso Denegado
        </h1>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          {message || 'No tienes permisos para acceder a esta seccion.'}
          {module && !message && (
            <span className="block mt-1 text-sm">
              Modulo: <strong>{module}</strong>
              {action && action !== 'view' && (
                <>, Accion: <strong>{action}</strong></>
              )}
            </span>
          )}
        </p>

        {/* Help text */}
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
          Si crees que deberias tener acceso, contacta a tu administrador.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Volver
          </button>

          {firstAccessibleHref && (
            <button
              onClick={() => navigate(firstAccessibleHref)}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 shadow-lg hover:shadow-xl transition-all"
            >
              <HomeIcon className="h-4 w-4" />
              Ir al inicio
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
